import React, { useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import * as S from "../../css/AddBarcode.styles";
import MenuComponent from "../MenuComponent";
import Loading from "../Loading";
import { loginState } from "../../stores/loginState";
import {
  useKpcManualScanBaseData,
  useKpcManualScans,
} from "../../api/addbarcode/useKpcManualScans";
import { formatNumberCommas } from "../../utils/formatNumberCommas";

const BARCODE_PATTERN = /^\d{11,13}$/;
// 요청 본문 크기 제한(2MB)보다 충분히 작게 유지하기 위한 전송 단위다.
const MAX_ITEMS_PER_REQUEST = 1000;
// KPC 수동등록 운송장은 선택한 운행일자의 13시 정각부터 입력 순서대로 스캔 시간을 부여한다.
// 다른 택배사의 기존 06시 시작 규칙과 분리하기 위해 KPC 전용 컴포넌트에만 선언한다.
const KPC_MANUAL_SCAN_START_TIME = "13:00:00.000";

const parseBarcodes = (value) =>
  value
    .split(/\r?\n/)
    .map((barcode) => barcode.trim())
    .filter(Boolean);

const isValidBarcodeList = (barcodes) =>
  barcodes.length > 0 && barcodes.every((barcode) => BARCODE_PATTERN.test(barcode));

function KpcManualScanRegistration({ theme }) {
  const login = useRecoilValue(loginState);
  const [scanDate, setScanDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [tmDv, setTmDv] = useState("700131");
  const [workerCode, setWorkerCode] = useState("");
  const [deviceCode, setDeviceCode] = useState("");
  const [barcodeText, setBarcodeText] = useState("");
  const [workers, setWorkers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [resultData, setResultData] = useState(null);
  const hasLoadedBaseData = useRef(false);

  const { mutateAsync: loadBaseData } = useKpcManualScanBaseData();
  const { mutateAsync: registerManualScans } = useKpcManualScans();

  useEffect(() => {
    // React Query 상태 변경으로 인한 중복 기본정보 요청을 방지한다.
    if (hasLoadedBaseData.current) {
      return;
    }
    hasLoadedBaseData.current = true;

    const load = async () => {
      try {
        const data = await loadBaseData();
        setWorkers(data.workers);
        setDevices(data.devices);
      } catch (error) {
        console.error("KPC 수동등록 기본정보 조회 실패", error);
        Swal.fire({
          icon: "error",
          title: "기본 정보를 불러오지 못했습니다.",
          text: error.message || "잠시 후 다시 시도해 주세요.",
          confirmButtonText: "확인",
        });
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [loadBaseData]);

  const selectedDevice = devices.find((device) => device.code === deviceCode);
  // KPC 서버가 업무구분에 따라 강제 설정하는 상대점소를 화면에도 동일하게 표시한다.
  const targetBranchCode = tmDv === "700112" ? "135" : "000";
  const barcodeList = parseBarcodes(barcodeText);
  const hasInvalidBarcode = barcodeList.some(
    (barcode) => !BARCODE_PATTERN.test(barcode),
  );

  const showApiResult = (data, title) => {
    setResultData(data);
    Swal.fire({
      icon: data.failureCount > 0 ? "warning" : "success",
      title,
      html: `전체 ${formatNumberCommas(data.totalCount || 0)}건<br/>성공 ${formatNumberCommas(data.successCount || 0)}건<br/>중복 ${formatNumberCommas(data.duplicateCount || 0)}건<br/>실패 ${formatNumberCommas(data.failureCount || 0)}건`,
      confirmButtonText: "확인",
    });
  };

  const handleRegister = async () => {
    if (!workerCode || !selectedDevice) {
      Swal.fire({
        icon: "warning",
        title: "사원과 장비를 선택해 주세요.",
        confirmButtonText: "확인",
      });
      return;
    }

    if (!isValidBarcodeList(barcodeList)) {
      Swal.fire({
        icon: "warning",
        title: "송장번호 형식이 올바르지 않습니다.",
        text: "송장번호는 한 줄에 하나씩 11~13자리 숫자만 입력해야 합니다.",
        confirmButtonText: "확인",
      });
      return;
    }

    setIsLoading(true);
    setResultData(null);

    try {
      // 선택 운행일자와 scan_total_time의 날짜를 동일하게 맞추고 KPC 기준 시각인 13시부터 시작한다.
      const startTime = dayjs(`${scanDate}T${KPC_MANUAL_SCAN_START_TIME}`);
      const items = barcodeList.map((barcode, index) => ({
        barcode,
        scandate: scanDate,
        scan_total_time: startTime.add(index, "second").format("YYYY-MM-DDTHH:mm:ss.SSS"),
      }));
      const responses = [];

      for (let index = 0; index < items.length; index += MAX_ITEMS_PER_REQUEST) {
        const response = await registerManualScans({
          tm_dv: tmDv,
          emp_cd: workerCode,
          dev_serial: selectedDevice.code,
          scanner_nm: selectedDevice.scannerName,
          camera_id: selectedDevice.cameraId,
          items: items.slice(index, index + MAX_ITEMS_PER_REQUEST),
        });

        if (response.result !== "00") {
          throw new Error(response.message || "수동등록에 실패했습니다.");
        }
        responses.push(response.data || {});
      }

      const mergedResult = responses.reduce(
        (result, response) => ({
          executionId: response.executionId || result.executionId,
          totalCount: result.totalCount + Number(response.totalCount || 0),
          successCount: result.successCount + Number(response.successCount || 0),
          duplicateCount: result.duplicateCount + Number(response.duplicateCount || 0),
          failureCount: result.failureCount + Number(response.failureCount || 0),
          items: [...result.items, ...(response.items || [])],
        }),
        {
          executionId: "",
          totalCount: 0,
          successCount: 0,
          duplicateCount: 0,
          failureCount: 0,
          items: [],
        },
      );

      showApiResult(mergedResult, "수동등록 처리 완료");
    } catch (error) {
      console.error("KPC 수동등록 실패", error);
      Swal.fire({
        icon: "error",
        title: "수동등록에 실패했습니다.",
        text: error.message || "잠시 후 다시 시도해 주세요.",
        confirmButtonText: "확인",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <S.DashboardContainer>
      <MenuComponent theme={theme} />
      <S.ContentContainer>
        {isLoading && <Loading />}
        <h4>운송장 추가등록</h4>
        <S.FlexContainer>
          <S.EditContainer style={{ width: "420px", flex: "0 0 420px" }}>
            <S.EditSpan>
              <h5>운행일자</h5>
              <S.Input type="date" value={scanDate} onChange={(event) => setScanDate(event.target.value)} />
            </S.EditSpan>
            <S.EditSpan>
              <h5>운행구분</h5>
              <S.SelectContainer value={tmDv} onChange={(event) => setTmDv(event.target.value)}>
                <option value="700131">일반입고 (700131)</option>
                <option value="700112">SLX집하 (700112)</option>
              </S.SelectContainer>
            </S.EditSpan>
            <S.EditSpan>
              <h5>상대점소</h5>
              <S.Input value={targetBranchCode} readOnly style={{ background: theme.white2 }} />
            </S.EditSpan>
            <S.EditSpan>
              <h5>작업영업점</h5>
              <S.Input value={login.branchCode || ""} readOnly style={{ background: theme.white2 }} />
            </S.EditSpan>
            <S.EditSpan>
              <h5>작업영업사원</h5>
              <S.SelectContainer value={workerCode} onChange={(event) => setWorkerCode(event.target.value)}>
                <option value="">선택해 주세요.</option>
                {workers.map((worker) => (
                  <option key={worker.code} value={worker.code}>
                    {worker.name} ({worker.code})
                  </option>
                ))}
              </S.SelectContainer>
            </S.EditSpan>
            <S.EditSpan>
              <h5>스캔 장비</h5>
              <S.SelectContainer value={deviceCode} onChange={(event) => setDeviceCode(event.target.value)}>
                <option value="">선택해 주세요.</option>
                {devices.map((device) => (
                  <option key={device.code} value={device.code}>
                    {device.scannerName} ({device.code})
                  </option>
                ))}
              </S.SelectContainer>
            </S.EditSpan>
            <h6>
              선택한 운행일자의 13시 0분 0초부터 송장번호 순서대로 1초 간격으로 등록됩니다.
              <br />
              점소·상대점소·차량번호·버전 정보는 로그인 세션과 업무구분을 기준으로 서버가 설정합니다.
            </h6>
          </S.EditContainer>

          <S.EditContainer style={{ width: "420px", flex: "0 0 420px" }}>
            <h5>운송장번호 등록</h5>
            <S.TextArea
              value={barcodeText}
              placeholder="송장번호를 한 줄에 하나씩 입력해 주세요."
              onChange={(event) => setBarcodeText(event.target.value.replace(/-/g, "").replace(/[^\d\n]/g, ""))}
            />
            {hasInvalidBarcode && <p style={{ color: "red" }}>송장번호는 한 줄에 하나씩 11~13자리 숫자만 입력해야 합니다.</p>}
            <p>검출된 운송장 {formatNumberCommas(barcodeList.length)} 건</p>
            <S.ActionBar>
              <S.ActionButton type="button" $variant="primary" onClick={handleRegister}>등록</S.ActionButton>
            </S.ActionBar>
          </S.EditContainer>
        </S.FlexContainer>

        {resultData && (
          <S.EditContainer style={{ width: "852px", maxWidth: "100%" }}>
            <h5>처리 결과</h5>
            <S.FileMeta>
              실행 ID: {resultData.executionId || "-"} / 전체 {formatNumberCommas(resultData.totalCount || 0)}건 / 성공 {formatNumberCommas(resultData.successCount || 0)}건 / 중복 {formatNumberCommas(resultData.duplicateCount || 0)}건 / 실패 {formatNumberCommas(resultData.failureCount || 0)}건
            </S.FileMeta>
            <S.HistoryTableWrap>
              <S.HistoryTable>
                <thead><tr><S.HistoryTh>송장번호</S.HistoryTh><S.HistoryTh>업무구분</S.HistoryTh><S.HistoryTh>스캔시각</S.HistoryTh><S.HistoryTh>처리결과</S.HistoryTh><S.HistoryTh>메시지</S.HistoryTh></tr></thead>
                <tbody>
                  {(resultData.items || []).map((item, index) => (
                    <tr key={`${item.barcode}-${index}`}><S.HistoryTd>{item.barcode || "-"}</S.HistoryTd><S.HistoryTd>{item.tm_dv || "-"}</S.HistoryTd><S.HistoryTd>{item.scan_total_time || "-"}</S.HistoryTd><S.HistoryTd>{item.outcome || "-"}</S.HistoryTd><S.HistoryTd>{item.message || "-"}</S.HistoryTd></tr>
                  ))}
                </tbody>
              </S.HistoryTable>
            </S.HistoryTableWrap>
          </S.EditContainer>
        )}
      </S.ContentContainer>
    </S.DashboardContainer>
  );
}

export default KpcManualScanRegistration;
